import { MenuItem, Stack, TextField, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { useMemo } from 'react';

import { LocationAutocomplete, useLocationAutocomplete } from '../LocationAutocomplete';
import type {
  PdfConfirmFieldName,
  PdfConfirmFieldValue,
  PdfPreviewField,
  PdfPreviewUnit,
} from '../../types/pdfPreview';
import { PDF_AMENITY_FIELDS, PDF_SHARED_FIELDS, PDF_UNIT_FIELDS } from '../../types/pdfPreview';
import { originalConfirmValue } from './pdfPreviewState';
import { pdfConfirmFieldOptions } from './pdfConfirmFields';

const priceFields = new Set<PdfConfirmFieldName>([
  'price',
  'sellingPrice',
  'priceDaily',
  'priceWeekly',
  'priceMonthly',
  'priceYearly',
]);
const areaFields = new Set<PdfConfirmFieldName>(['builtUpArea', 'plotArea']);
const readable = (name: string) =>
  name.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase());
const describe = (value: unknown): string => {
  if (value === null || value === undefined) return 'not provided';
  if (Array.isArray(value)) return value.map(describe).join(', ');
  if (typeof value !== 'object') return String(value);
  const item = value as Record<string, unknown>;
  if ('sqft' in item)
    return `${item.sqft ?? [item.amount ?? '?', item.upperAmount ?? '?'].join('–')} ${item.sqft ? 'sqft' : item.unit ?? 'area'}${item.qualifier ? ` (${item.qualifier})` : ''}`;
  if ('amount' in item || 'upperAmount' in item)
    return `${item.upperAmount ? `${item.amount ?? '?'}–${item.upperAmount}` : item.amount ?? '?'} ${item.currency ?? ''} ${item.period ?? ''}${item.qualifier ? ` (${item.qualifier})` : ''}`.trim();
  return 'value requires review';
};
const evidence = (field?: PdfPreviewField) => {
  if (!field) return 'Missing from source';
  const pages = Array.from(
    new Set(
      [...field.sources, ...field.suggestions.flatMap((item) => item.sources)].map(
        (source) => source.pageNumber
      )
    )
  );
  const suggestions = field.suggestions
    .map((item) => `${item.state}: ${describe(item.value)}`)
    .join('; ');
  return [
    field.state,
    field.value !== null ? `Found: ${describe(field.value)}` : '',
    field.needsReview ? 'Review required' : '',
    suggestions,
    pages.length ? `Page ${pages.join(', ')}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
};

type Source = Pick<PdfPreviewUnit, 'fields' | 'location' | 'review'>;
type Props = {
  source: Source;
  values: (field: PdfConfirmFieldName) => PdfConfirmFieldValue;
  onChange: (field: PdfConfirmFieldName, value: PdfConfirmFieldValue) => void;
  shared?: boolean;
  disabled: boolean;
};

const PdfLocation = ({
  source,
  value,
  onChange,
  disabled,
}: {
  source: Source;
  value: PdfConfirmFieldValue;
  onChange: (value: PdfConfirmFieldValue) => void;
  disabled: boolean;
}) => {
  const location = useLocationAutocomplete({
    defaultValue: source.location.nameEn ?? '',
    skipInitialSearch: true,
  });
  const candidates = useMemo(
    () => location.locations.filter((item) => ['level1', 'level2', 'level3'].includes(item.kind)),
    [location.locations]
  );
  return (
    <Stack spacing={0.5}>
      <Typography variant='body2'>
        Location: {source.location.status.replace(/_/g, ' ')}
        {source.location.nameEn ? ` · ${source.location.nameEn}` : ''}
      </Typography>
      <Typography variant='caption'>Canonical ID: {value ?? 'not selected'}</Typography>
      <LocationAutocomplete
        ariaLabel='Search canonical location'
        locations={candidates}
        inputAddressValue={location.inputAddressValue}
        setInputAddressValue={location.setInputAddressValue}
        isLoading={location.isLoadingLocations}
        isDisable={disabled}
        autoSelectFirstOnEnter={false}
        clearOnDependenciesChange={false}
        onInputChange={() => onChange(null)}
        onSelect={(selected) => onChange(selected.id)}
        handleClear={() => onChange(null)}
      />
      {source.location.reviewCodes.map((code) => (
        <Typography color='warning.main' key={code} variant='caption'>
          {code}
        </Typography>
      ))}
    </Stack>
  );
};

export const PdfPreviewFields = ({ source, values, onChange, shared = false, disabled }: Props) => {
  const fields = shared ? PDF_SHARED_FIELDS : PDF_UNIT_FIELDS;
  return (
    <Stack spacing={2}>
      <Typography variant='body2'>
        Confirmed values come from the PDF. Empty fields require your review. Changes stay on this
        page until you leave.
      </Typography>
      <Stack direction='row' flexWrap='wrap' gap={2}>
        {fields.map((field) => {
          const current = values(field);
          const original = originalConfirmValue(source, field);
          const reviewCodes = source.review
            .filter((item) => item.field === field)
            .map((item) => item.code);
          if (field === 'locationId') {
            return (
              <Stack key={field} sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <PdfLocation
                  source={source}
                  value={current}
                  onChange={(value) => onChange(field, value)}
                  disabled={disabled}
                />
              </Stack>
            );
          }
          if (
            field === 'postHandover' ||
            (PDF_AMENITY_FIELDS as readonly string[]).includes(field)
          ) {
            return (
              <Stack key={field} sx={{ flex: '1 1 180px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={current === true}
                      indeterminate={current === null}
                      disabled={disabled}
                      onChange={() =>
                        onChange(field, current === null ? true : current === true ? false : null)
                      }
                    />
                  }
                  label={`${readable(field)} (${current === null ? 'not specified' : current ? 'yes' : 'no'})`}
                />
                <Typography variant='caption'>
                  {evidence(source.fields[field])} · Click to cycle yes, no and unspecified.
                </Typography>
              </Stack>
            );
          }
          const options = pdfConfirmFieldOptions[field];
          const text =
            current === null ? '' : Array.isArray(current) ? current.join(', ') : String(current);
          const invalidPrice =
            priceFields.has(field) &&
            text !== '' &&
            (!/^(0|[1-9]\d{0,9})$/.test(text) || Number(text) > 2147483647);
          const invalidArea =
            areaFields.has(field) &&
            text !== '' &&
            (!Number.isInteger(Number(text)) || Number(text) < 1 || Number(text) > 2147483647);
          const invalidYear = field === 'handoverYear' && text !== '' && !/^\d{4}$/.test(text);
          const invalidView =
            field === 'view' &&
            Array.isArray(current) &&
            (current.length > 32 ||
              current.some((item) => item.length > 120 || /https?:|@/.test(item)));
          return (
            <Stack
              key={field}
              sx={{ flex: field === 'description' ? '1 1 100%' : '1 1 220px', minWidth: 0 }}
              spacing={0.5}
            >
              <TextField
                fullWidth
                label={readable(field)}
                value={text}
                disabled={disabled}
                select={Boolean(options)}
                multiline={field === 'description'}
                minRows={field === 'description' ? 4 : undefined}
                error={invalidPrice || invalidArea || invalidYear || invalidView}
                inputProps={{
                  maxLength: field === 'description' ? 8000 : field === 'title' ? 256 : undefined,
                  min: areaFields.has(field) ? 1 : undefined,
                  max: areaFields.has(field) ? 2147483647 : undefined,
                  step: areaFields.has(field) ? 1 : undefined,
                }}
                type={areaFields.has(field) ? 'number' : undefined}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (field === 'view')
                    onChange(
                      field,
                      raw
                        ? raw
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean)
                        : []
                    );
                  else if (areaFields.has(field)) onChange(field, raw === '' ? null : Number(raw));
                  else onChange(field, raw === '' ? null : raw);
                }}
              >
                {options && (
                  <MenuItem value=''>
                    <em>Not selected</em>
                  </MenuItem>
                )}
                {options?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {readable(option)}
                  </MenuItem>
                ))}
              </TextField>
              <Typography
                color={source.fields[field]?.needsReview ? 'warning.main' : 'text.secondary'}
                variant='caption'
              >
                {evidence(source.fields[field])}
                {reviewCodes.length ? ` · ${reviewCodes.join(', ')}` : ''}
                {original !== null && JSON.stringify(original) !== JSON.stringify(current)
                  ? ` · Original: ${Array.isArray(original) ? original.join(', ') : String(original)}`
                  : ''}
                {areaFields.has(field) ? ' · sqft' : ''}
                {invalidPrice ? ' · Enter a whole amount from 0 to 2147483647' : ''}
                {invalidArea ? ' · Enter a whole area from 1 to 2147483647 sqft' : ''}
                {invalidYear ? ' · Enter a four-digit year' : ''}
                {invalidView ? ' · Review view entries (up to 32, 120 characters each)' : ''}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      {source.review
        .filter((item) => !(fields as readonly string[]).includes(item.field))
        .map((item, index) => (
          <Typography color='warning.main' key={`${item.code}-${index}`} variant='body2'>
            Review: {item.code} ({item.field})
          </Typography>
        ))}
    </Stack>
  );
};
